import { z } from 'zod'

/**
 * アクセスログ閲覧の Zod スキーマ。
 *
 * - PRO: フルデータ
 * - FREE: マスク済み（個人特定情報は **** に置換）
 * - EPHEMERAL: 自身の共有URL経由のみ閲覧可
 *
 * 関連: docs/system-requirements.md §4.6 / packages/db/prisma/schema.prisma の AccessLog
 */

export const AccessActionSchema = z.enum([
  'VIEW',
  'PLAY_START',
  'PLAY_PROGRESS',
  'PLAY_END',
  'DOWNLOAD',
  'UPLOAD',
  'ARCHIVE',
  'UNARCHIVE',
  'DELETE',
])
export type AccessAction = z.infer<typeof AccessActionSchema>

export const ActorTypeSchema = z.enum(['OWNER', 'GUEST', 'ADMIN'])
export type ActorType = z.infer<typeof ActorTypeSchema>

// ---- Path / Query ----

export const ListAccessLogsQuerySchema = z.object({
  action: AccessActionSchema.optional(),
  fromDate: z.string().datetime().optional(),
  toDate: z.string().datetime().optional(),
  fileId: z.string().uuid().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
})
export type ListAccessLogsQuery = z.infer<typeof ListAccessLogsQuerySchema>

// ---- Items ----

const FullAccessLogSchema = z.object({
  id: z.string(),
  createdAt: z.string().datetime(),
  action: AccessActionSchema,
  actorType: ActorTypeSchema,
  actorId: z.string().nullable(),
  projectId: z.string().uuid().nullable(),
  fileId: z.string().uuid().nullable(),
  /**
   * 対象ファイルの original_name。
   * - log 書込み時のファイル名なので、後でファイルが renamed されても当時の値を返す
   *   …のが理想だが、現状 schema に保存していないため LEFT JOIN で現在値を表示する。
   * - DELETE 等で file レコードが消えていれば null。
   */
  fileName: z.string().nullable(),
  /** 対象ファイルの MIME type（フロントで種別アイコン表示するため）。null = 削除済み or 対象ファイルなし */
  fileMimeType: z.string().nullable(),
  shareLinkId: z.string().uuid().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  viewerEmail: z.string().nullable(),
  viewerAnonymousId: z.string().nullable(),
  playPositionSec: z.number().int().nullable(),
  playedSeconds: z.number().int().nullable(),
})
export type FullAccessLog = z.infer<typeof FullAccessLogSchema>

const MaskedAccessLogSchema = z.object({
  id: z.string(),
  createdAt: z.string().datetime(),
  action: AccessActionSchema,
  actorType: ActorTypeSchema,
  // 個人特定情報はマスク
  actorId: z.literal('****').nullable(),
  projectId: z.string().uuid().nullable(),
  fileId: z.string().uuid().nullable(),
  // ファイル名・種別はマスクしない（業務識別に必要、個人特定情報ではない）
  fileName: z.string().nullable(),
  fileMimeType: z.string().nullable(),
  shareLinkId: z.string().uuid().nullable(),
  ip: z.literal('****').nullable(),
  userAgent: z.literal('****').nullable(),
  viewerEmail: z.literal('****').nullable(),
  viewerAnonymousId: z.literal('****').nullable(),
  playPositionSec: z.number().int().nullable(),
  playedSeconds: z.number().int().nullable(),
})
export type MaskedAccessLog = z.infer<typeof MaskedAccessLogSchema>

// ---- Response（プラン別ユニオン） ----

export const ListAccessLogsResultSchema = z.object({
  /** 'full' なら PRO/EPHEMERAL（自身の範囲）、'masked' なら FREE */
  mode: z.enum(['full', 'masked']),
  items: z.union([z.array(FullAccessLogSchema), z.array(MaskedAccessLogSchema)]),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
})
export type ListAccessLogsResult = z.infer<typeof ListAccessLogsResultSchema>
